import React from 'react';
import { useLocation } from 'wouter';
import { useTranslation } from 'react-i18next';
import StockImageModal from '@/components/StockImageModal';

export default function BlogNew() {
  const { t } = useTranslation();
  const [, setLocation] = useLocation();
  const [title, setTitle] = React.useState('');
  const [imageUrl, setImageUrl] = React.useState('');
  const [author, setAuthor] = React.useState('');
  const [publishedAt, setPublishedAt] = React.useState<string>('');
  const [excerpt, setExcerpt] = React.useState('');
  const [content, setContent] = React.useState('');
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [showImageModal, setShowImageModal] = React.useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const res = await fetch('/api/articles', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          title,
          excerpt,
          content,
          imageUrl: imageUrl || null,
          author,
          publishedAt: publishedAt || undefined,
          isPublished: true,
        }),
      });
      const contentType = res.headers.get('content-type') || '';
      if (!res.ok) {
        const errText = await res.text();
        throw new Error(`HTTP ${res.status} ${res.statusText} - ${errText.slice(0, 120)}`);
      }
      if (!contentType.includes('application/json')) {
        const text = await res.text();
        throw new Error(`Unexpected response (content-type: ${contentType}). First bytes: ${text.slice(0, 60)}`);
      }
      const article = await res.json();
      setLocation(`/blog/${article.slug}`);
    } catch (err: any) {
      setError(err?.message || 'Error');
    } finally {
      setSubmitting(false);
    }
  };

  const imageTags = React.useMemo(() => {
    return title.split(/\s+/).filter(Boolean).slice(0, 6);
  }, [title]);

  return (
    <div className="container mx-auto px-4 py-8 max-w-3xl">
      <h1 className="text-3xl font-bold mb-6">Crear entrada de blog</h1>
      {error && (
        <div className="mb-4 p-3 rounded bg-red-50 border border-red-200 text-red-700 text-sm">{error}</div>
      )}
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium mb-1">Título</label>
          <input value={title} onChange={(e) => setTitle(e.target.value)} required
                 className="w-full border rounded px-3 py-2" placeholder="Título del post" />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Imagen</label>
          {imageUrl ? (
            <div className="mb-2">
              <img src={imageUrl} alt="Seleccionada" className="w-full max-h-64 object-cover rounded border" />
            </div>
          ) : null}
          <div className="flex gap-2">
            <button type="button" onClick={() => setShowImageModal(true)}
                    className="px-4 py-2 bg-slate-200 rounded hover:bg-slate-300">Seleccionar imagen</button>
            {imageUrl && (
              <button type="button" onClick={() => setImageUrl('')}
                      className="px-4 py-2 bg-slate-200 rounded hover:bg-slate-300">Quitar</button>
            )}
          </div>
          <StockImageModal
            isOpen={showImageModal}
            onClose={() => setShowImageModal(false)}
            onSelect={(img: any) => setImageUrl(img.url)}
            initialQuery={title || 'analytics'}
            tags={imageTags}
          />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">Autor</label>
            <input value={author} onChange={(e) => setAuthor(e.target.value)} required
                   className="w-full border rounded px-3 py-2" placeholder="Nombre" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Fecha publicación</label>
            <input type="date" value={publishedAt} onChange={(e) => setPublishedAt(e.target.value)}
                   className="w-full border rounded px-3 py-2" />
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Descripción corta</label>
          <textarea value={excerpt} onChange={(e) => setExcerpt(e.target.value)} rows={3}
                    className="w-full border rounded px-3 py-2" placeholder="Resumen" />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Contenido (Markdown o HTML)</label>
          <textarea value={content} onChange={(e) => setContent(e.target.value)} rows={10} required
                    className="w-full border rounded px-3 py-2 font-mono" placeholder={"Escribe en Markdown (**, ##, listas) o pega HTML simple"} />
          <p className="text-xs text-gray-500 mt-1">Soporta Markdown (GFM) y HTML simple. Ejemplos: **negrita**, ## Título, listas, enlaces.</p>
        </div>
        <div className="pt-2">
          <button disabled={submitting} className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-50">
            {submitting ? 'Guardando…' : 'Publicar' }
          </button>
        </div>
      </form>
    </div>
  );
}


